import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Icon } from 'semantic-ui-react'
import { getPhotoCollection } from 'core/api/photo'

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Modal = styled.div`
  background: white;
  border-radius: 6px;
  width: 580px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #555;
  z-index: 1;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #000;
  }
`

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 16px 16px 0;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  flex-shrink: 0;
`

const Tab = styled.button`
  padding: 8px 14px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: ${props => props.active ? 'bold' : 'normal'};
  color: ${props => props.active ? '#0a82fe' : '#555'};
  border-bottom: 2px solid ${props => props.active ? '#0a82fe' : 'transparent'};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    color: #0a82fe;
  }
`

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  flex: 1;
`

const PhotoCell = styled.div`
  position: relative;
  aspect-ratio: 1;
  border: 2px solid ${props => props.selected ? '#0a82fe' : '#ddd'};
  border-radius: 4px;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: ${props => props.disabled ? '#ddd' : '#0a82fe'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${props => props.disabled ? 0.4 : 1};
  }
`

const AlreadySentBadge = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 0.7em;
  text-align: center;
  padding: 3px 0;
`

const Checkmark = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  background: #0a82fe;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #888;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #888;
  font-size: 0.95em;
`

const PhotoCollectionModal = ({ conversationUid, onSelect, onClose }) => {
  const [categories, setCategories] = useState([])
  const [activeCategory, setActiveCategory] = useState(null)
  const [photos, setPhotos] = useState([])
  const [selectedPhoto, setSelectedPhoto] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadCategory(null)
  }, [])

  const loadCategory = async(categoryId) => {
    setLoading(true)
    setSelectedPhoto(null)
    const result = await getPhotoCollection(conversationUid, categoryId)

    if (!categories.length) {
      setCategories(result.categories)
    }

    setPhotos(result.photos)
    setLoading(false)

    if (!categoryId && result.categories.length) {
      setActiveCategory(result.categories[0].id)
      const firstCat = await getPhotoCollection(conversationUid, result.categories[0].id)
      setPhotos(firstCat.photos)
    }
  }

  const handleTabClick = (categoryId) => {
    setActiveCategory(categoryId)
    loadCategoryPhotos(categoryId)
  }

  const loadCategoryPhotos = async(categoryId) => {
    setLoading(true)
    setSelectedPhoto(null)
    const result = await getPhotoCollection(conversationUid, categoryId)
    setPhotos(result.photos)
    setLoading(false)
  }

  const handlePhotoClick = (photo) => {
    if (photo.alreadySent) {
      return
    }

    if (selectedPhoto?.id === photo.id) {
      setSelectedPhoto(null)
    } else {
      setSelectedPhoto(photo)
      onSelect(photo)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <Overlay onClick={handleOverlayClick}>
      <Modal>
        <CloseButton onClick={onClose}>&times;</CloseButton>

        <TabBar>
          {categories.map(cat => (
            <Tab
              key={cat.id}
              active={activeCategory === cat.id}
              onClick={() => handleTabClick(cat.id)}
            >
              {cat.name}
            </Tab>
          ))}
        </TabBar>

        {loading ? (
          <LoadingContainer>Loading photos...</LoadingContainer>
        ) : photos.length === 0 ? (
          <EmptyState>
            <Icon name="image outline" size="big"/>
            <p>No available photos in this category</p>
          </EmptyState>
        ) : (
          <PhotoGrid>
            {photos.map(photo => (
              <PhotoCell
                key={photo.id}
                selected={selectedPhoto?.id === photo.id}
                disabled={photo.alreadySent}
                onClick={() => handlePhotoClick(photo)}
              >
                <img src={photo.url} alt=""/>
                {selectedPhoto?.id === photo.id && (
                  <Checkmark>&#10003;</Checkmark>
                )}
                {photo.alreadySent && (
                  <AlreadySentBadge>Already sent</AlreadySentBadge>
                )}
              </PhotoCell>
            ))}
          </PhotoGrid>
        )}
      </Modal>
    </Overlay>
  )
}

export default PhotoCollectionModal
